import { clampTo, hasImageInput } from '../../tools';
import type { suchuangModelConfig } from '../types';

const GPT_IMAGE_25_PIXEL_SIZE = {
    '1K': {
        '1:1': '1024x1024',
        '16:9': '1280x720',
        '9:16': '720x1280',
        '4:3': '1152x864',
        '3:4': '864x1152',
        '3:2': '1536x1024',
        '2:3': '1024x1536',
        '5:4': '1120x896',
        '4:5': '896x1120',
        '21:9': '1456x624',
        '9:21': '624x1456',
        '1:3': '688x2048',
        '3:1': '2048x688',
        '2:1': '1536x768',
        '1:2': '768x1536',
    },
    '2K': {
        '1:1': '2048x2048',
        '16:9': '2048x1152',
        '9:16': '1152x2048',
        '4:3': '2304x1728',
        '3:4': '1728x2304',
        '3:2': '2048x1360',
        '2:3': '1360x2048',
        '5:4': '2240x1792',
        '4:5': '1792x2240',
        '21:9': '2912x1248',
        '9:21': '1248x2912',
        '1:3': '688x2048',
        '3:1': '2048x688',
        '2:1': '3072x1536',
        '1:2': '1536x3072',
    },
    '4K': {
        '1:1': '2880x2880',
        '16:9': '3840x2160',
        '9:16': '2160x3840',
        '4:3': '3264x2448',
        '3:4': '2448x3264',
        '3:2': '3504x2336',
        '2:3': '2336x3504',
        '5:4': '3200x2560',
        '4:5': '2560x3200',
        '21:9': '3840x1648',
        '9:21': '1648x3840',
        '1:3': '1280x3840',
        '3:1': '3840x1280',
        '2:1': '3840x1920',
        '1:2': '1920x3840',
    },
} as const;

export const openaiConfigs: Record<string, suchuangModelConfig> = {
    'gpt-image-2.5-sunburst': {
        // https://api.wuyinkeji.com/doc/79
        apiPath: '/api/async/image_gpt_2.5_sunburst',
        pollApiPath: '/api/async/detail?id=${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            const pixelSize = GPT_IMAGE_25_PIXEL_SIZE[clampTo(
                (image_resolution || '1K').toUpperCase() as keyof typeof GPT_IMAGE_25_PIXEL_SIZE,
                ['1K', '2K', '4K'] as const,
                '1K',
            )][clampTo(
                (aspectRatio || '1:1') as keyof typeof GPT_IMAGE_25_PIXEL_SIZE['1K'],
                ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '5:4', '4:5', '21:9', '9:21', '1:3', '3:1', '2:1', '1:2'] as const,
                '1:1',
            )];
            if (hasImage) {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                    // 图像 URL
                    urls: image_urls.slice(0, 16).join(','),
                    // 可选参数
                    quality: clampTo(providerOptions?.quality || 'medium', ['low', 'medium', 'high', 'xhigh', 'max'] as const, 'medium'),
                    background: clampTo(providerOptions?.background || 'auto', ['auto', 'transparent'] as const, 'auto'),
                    // system
                    mask: undefined,
                };
            } else {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                    // 可选参数
                    quality: clampTo(providerOptions?.quality || 'medium', ['low', 'medium', 'high', 'xhigh', 'max'] as const, 'medium'),
                    background: clampTo(providerOptions?.background || 'auto', ['auto', 'transparent'] as const, 'auto'),
                };
            }
        },
    },
    'gpt-image-2.5-flare': {
        // https://api.wuyinkeji.com/doc/80
        apiPath: '/api/async/image_gpt_2.5_flare',
        pollApiPath: '/api/async/detail?id=${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            const pixelSize = GPT_IMAGE_25_PIXEL_SIZE[clampTo(
                (image_resolution || '1K').toUpperCase() as keyof typeof GPT_IMAGE_25_PIXEL_SIZE,
                ['1K', '2K', '4K'] as const,
                '1K',
            )][clampTo(
                (aspectRatio || '1:1') as keyof typeof GPT_IMAGE_25_PIXEL_SIZE['1K'],
                ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '5:4', '4:5', '21:9', '9:21', '1:3', '3:1', '2:1', '1:2'] as const,
                '1:1',
            )];
            if (hasImage) {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                    // 图像 URL
                    urls: image_urls.slice(0, 16).join(','),
                    // 可选参数
                    quality: clampTo(providerOptions?.quality || 'medium', ['low', 'medium', 'high'] as const, 'medium'),
                    background: clampTo(providerOptions?.background || 'auto', ['auto', 'transparent'] as const, 'auto'),
                    // system
                    mask: undefined,
                };
            } else {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                    // 可选参数
                    quality: clampTo(providerOptions?.quality || 'medium', ['low', 'medium', 'high'] as const, 'medium'),
                    background: clampTo(providerOptions?.background || 'auto', ['auto', 'transparent'] as const, 'auto'),
                };
            }
        },
    },
    'gpt-image-2.5': {
        // https://api.wuyinkeji.com/doc/78
        apiPath: '/api/async/image_gpt_2.5',
        pollApiPath: '/api/async/detail?id=${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio } = params;
            const hasImage = hasImageInput(image_urls);
            const pixelSize = GPT_IMAGE_25_PIXEL_SIZE['1K'][clampTo(
                (aspectRatio || '1:1') as keyof typeof GPT_IMAGE_25_PIXEL_SIZE['1K'],
                ['1:1', '16:9', '9:16', '4:3', '3:4', '3:2', '2:3', '5:4', '4:5', '21:9', '9:21', '1:3', '3:1', '2:1', '1:2'] as const,
                '1:1',
            )];
            if (hasImage) {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                    // 图片 URL
                    urls: image_urls.slice(0, 16).join(','),
                };
            } else {
                return {
                    prompt: prompt,
                    aspectRatio: pixelSize,
                };
            }
        },
    },
    'gpt-image-2': {
        // https://api.wuyinkeji.com/doc/53
        apiPath: '/api/async/image_gpt',
        pollApiPath: '/api/async/detail?id=${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    prompt: prompt,
                    size: aspectRatio === 'match' ? 'auto' : clampTo( aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '16:9' ),
                    // 图片 URL
                    urls: image_urls.slice(0, 16),
                };
            } else {
                return {
                    prompt: prompt,
                    size: clampTo( aspectRatio || '16:9', ['21:9', '9:21', '16:9', '9:16', '4:3', '3:4', '3:1', '1:3', '3:2', '2:3', '2:1', '1:2', '1:1'] as const, '16:9' ),
                };
            }
        },
    },
};
